import { useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import type React from 'react';

import { Elements } from '@stripe/react-stripe-js';
import type { StripeElementsOptionsMode } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { getStripeKey } from '#config/stripe';

import { useLocale } from '#src/app/shared/hooks/l10n';

import { toStripeLocale } from '../utils/toStripeLocale';

type Props = {
	options: StripeElementsOptionsMode;
	appearance?: StripeElementsOptionsMode['appearance'];
};

// Raw token values mirrored from @change/ds-tokens (see package.json for current version)
// so the Stripe iframe matches surrounding input field styling from createDivInputStyles.
// Stripe's Elements API does not accept CSS custom properties, so these must be literal values.
// Light mode only — dark mode is not handled by this experiment.
// If ds-tokens updates these values, update them here too.
export const DEFAULT_STRIPE_APPEARANCE = {
	theme: 'stripe',
	variables: {
		borderRadius: '8px',
		colorText: '#2d2f37',
		colorTextPlaceholder: 'rgba(58, 62, 73, 0.8)',
		fontSizeBase: '14px',
	},
	rules: {
		'.Input': {
			border: '1px solid #6f7381',
			boxShadow: 'none',
			padding: '10px 12px',
		},
		'.Input:focus': {
			border: '1px solid transparent',
			boxShadow: '0 0 0 2px #4771ff',
			outline: 'none',
		},
	},
} as const;

export const StripePaymentElementsProvider: React.FC<PropsWithChildren<Props>> = ({
	options,
	appearance,
	children,
}) => {
	const utilityContext = useUtilityContext();
	const environment = utilityContext.environment.getApiEnvironment();
	const subEnvironment =
		utilityContext.environment.getApiDemoEnvironmentTarget() ?? utilityContext.environment.getApiSubEnvironment();
	const stripeKey = getStripeKey({ environment, subEnvironment });
	const locale = useLocale();
	// Memoize the Stripe promise so it isn't recreated on every render. `async` is required
	// by @typescript-eslint/promise-function-async; the outer async function auto-unwraps
	// the returned Promise so the resolved type is still Promise<Stripe | null>.
	// eslint-disable-next-line react-hooks/use-memo
	const stripePromise = useMemo(async () => loadStripe(stripeKey), [stripeKey]);

	return (
		// mode is immutable after mount (not in StripeElementsUpdateOptions); keying on it
		// forces a remount when the user switches payment term (one-time ↔ recurring).
		// locale is also immutable after mount — runtime locale changes won't update the iframe.
		// amount is NOT keyed here: react-stripe-js calls elements.update({ amount }) on every
		// options.amount change, which Stripe propagates to wallet UIs (Google Pay, Apple Pay).
		<Elements
			key={options.mode}
			stripe={stripePromise}
			options={{
				...options,
				locale: toStripeLocale(locale),
				paymentMethodCreation: 'manual',
				appearance: appearance ?? DEFAULT_STRIPE_APPEARANCE,
			}}
		>
			{children}
		</Elements>
	);
};
